import React from "react";
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import moment from "moment";
import { findCust, findStrategy, formatValueDecimal, hashInvestment } from "../../component/Methods";
import {
  PositionsColgroup,
  lotColumnLabel,
  lotHeaderCellSx,
} from "./positionsTableConstants";

/**
 * Lot rows nested under a position row; uses the same colgroup as the parent table for alignment.
 */
const PositionsLotsExpandedTable = ({ theme, isOpen, lots, strategies, custodians }) => {
  const h = (id) => lotColumnLabel(id);
  const sx = lotHeaderCellSx(theme);

  return (
    <Collapse in={isOpen} timeout="auto" unmountOnExit>
      <Box
        sx={{
          backgroundColor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            width: "100%",
            backgroundColor: theme.palette.grey[50],
          }}
        >
          <PositionsColgroup />
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...sx, border: 0 }} />
              <TableCell align="left" sx={{ ...sx, border: 0 }}>
                {h("position")?.label ?? "Position"}
              </TableCell>
              <TableCell align="left" sx={{ ...sx, border: 0 }}>
                {h("date")?.label ?? "Date"}
              </TableCell>
              <TableCell align="left" sx={{ ...sx, border: 0 }}>
                Investment
              </TableCell>
              <TableCell sx={{ ...sx, border: 0 }} />
              <TableCell align="left" sx={{ ...sx, border: 0 }}>
                {h("strategy")?.label ?? "Strategy"}
              </TableCell>
              <TableCell align="left" sx={{ ...sx, border: 0 }}>
                {h("custodian")?.label ?? "Custodian"}
              </TableCell>
              <TableCell align="right" sx={{ ...sx, border: 0 }}>
                {h("shareCount")?.label ?? "Share Count"}
              </TableCell>
              <TableCell align="right" sx={{ ...sx, border: 0 }}>
                {h("costPerShare")?.label ?? "Cost Per Share"}
              </TableCell>
              <TableCell align="right" sx={{ ...sx, border: 0 }}>
                {h("sharePrice")?.label ?? "Share Price"}
              </TableCell>
              <TableCell sx={{ ...sx, border: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lots.map((lot, lotIndex) => (
              <TableRow
                key={lotIndex}
                sx={{
                  "&:hover": {
                    backgroundColor: theme.palette.grey[100],
                  },
                }}
              >
                <TableCell sx={{ border: 0 }} />
                <TableCell
                  sx={{
                    border: 0,
                    color: theme.palette.text.secondary,
                    fontSize: "0.8125rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lot.position === 0 ? "Long" : "Short"}
                </TableCell>
                <TableCell
                  sx={{
                    border: 0,
                    color: theme.palette.text.secondary,
                    fontSize: "0.8125rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lot.date ? moment(lot.date).format("MMM DD, YYYY") : "-"}
                </TableCell>
                <TableCell
                  sx={{
                    border: 0,
                    color: theme.palette.text.secondary,
                    fontSize: "0.8125rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {hashInvestment(Number(lot.ticker?.investment)) || "-"}
                </TableCell>
                <TableCell sx={{ border: 0 }} />
                <TableCell
                  sx={{
                    border: 0,
                    color: theme.palette.text.secondary,
                    fontSize: "0.8125rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {findStrategy(lot.strategy, strategies) || "-"}
                </TableCell>
                <TableCell
                  sx={{
                    border: 0,
                    color: theme.palette.text.secondary,
                    fontSize: "0.8125rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {findCust(lot.account?.custodian, custodians) || "-"}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    border: 0,
                    color: theme.palette.text.secondary,
                    fontSize: "0.8125rem",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lot.shareCount < 0
                    ? `(${Math.abs(lot.shareCount).toLocaleString()})`
                    : lot.shareCount.toLocaleString()}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    border: 0,
                    color: theme.palette.text.secondary,
                    fontSize: "0.8125rem",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lot.costPerShare < 0
                    ? `(${formatValueDecimal(Math.abs(lot.costPerShare))})`
                    : formatValueDecimal(lot.costPerShare)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    border: 0,
                    color: theme.palette.text.secondary,
                    fontSize: "0.8125rem",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {lot.sharePrice < 0
                    ? `(${formatValueDecimal(Math.abs(lot.sharePrice))})`
                    : formatValueDecimal(lot.sharePrice)}
                </TableCell>
                <TableCell sx={{ border: 0 }} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Collapse>
  );
};

export default PositionsLotsExpandedTable;
